import { Fetcher } from '../api/impl/utils/Fetcher';
import { DelayedAction, PurchaseInfo } from '../api/ReplicantAPI';
import { ActionAnalyticsEvent } from '../common/Analytics';
import { ErrorReporter } from '../common/ErrorReporter';
import { FriendsStatesMap } from '../common/FriendsStatesMap';
import type { ABTestsDynamicConfig } from '../common/ReplicantABTests';
import {
    APPLY_MESSAGES_ACTION,
    ARGS_TOO_LARGE_WARNING_LIMIT_BYTES,
    HEARTBEAT_ACTION,
    MAX_CLIENT_SYNC_CLOCK_OFFSET,
    MAX_LARGE_CLIENT_SYNC_CLOCK_OFFSET,
} from '../common/ReplicantConstants';
import type { UserMessageItem } from '../common/Types';
import type { ChangesetResult, ChatbotMetainfo, default as DB, Entry, EntryDiff, MetainfoMVCC } from '../db/DB';
import { createActionArgsTooLargeWarning, ReplicantError, ReplicantErrorCode, ReplicantErrorSubCode } from '../Errors';
import logger from '../logger';
import { generateIndexDoc } from '../ReplicantComputedProperties';
import { Replicant, ReplicantConfig } from '../ReplicantConfig';
import { Message } from '../ReplicantMessages';
import { Ruleset } from '../ReplicantRuleset';
import SB from '../SchemaBuilder';
import { addNonEnumerableSystemStateFields, getSystemStateFieldsFromEntry } from '../systemStateFields';
import {
    getUnreducedMessagesForDeletion,
    isMessageRecentlyReduced,
    updateRecentlyReducedMessages,
} from '../utils/MessageUtils';
import { deepCopy, deepEquals, getNamespacedValue } from '../utils/ObjUtils';
import { duration, isPromise } from '../utils/Utils';
import { createVirtualEntry, VirtualEntry } from '../utils/VirtualState';
import * as perf from './performanceTracking';
import type {
    APIAccessInterface,
    ApplyMessagesToStateFn,
    CreateActionAPIFn,
    ExecutionContext,
    ReplicationEntry,
    WriteChangesetFN,
} from './ServerReplicant';
import { updateSnapshotTimestamps } from './StateRecovery';
import { wrapAPI } from './utils/wrapAPI';

/**
 * Information about the client's current metainfo properties.
 *
 * The server compares these properties with the latest known metainfo and includes the resulting delta in its response, syncing metainfo between client and server.
 *
 * Metainfo properties can change outside of the client-server process for example in chatbot webhook handlers.
 */
export type ClientMetainfoState = {
    chatbotPurchaseHistoryLength?: number;
};

export type ReplicationResult<TUserState, TRuleset extends Ruleset> = {
    results: ChangesetResult[][];
    messages: UserMessageItem[];
    rev: number;
    requestedProfiles?: FriendsStatesMap<TUserState, TRuleset>;

    /** Include mutated metainfo properties in replication response to keep client metainfo in sync with backend metainfo. */
    metainfoDelta?: {
        hasPhoneNumber?: boolean;
        paymentSubscriptionStatus?: MetainfoMVCC['paymentSubscription'];
        purchaseHistory?: PurchaseInfo[];
        chatbotPurchaseHistory?: PurchaseInfo[];
    };

    /** Timestamp that server used to process this batch. */
    lastUpdated: number;

    /** Timestamp after processing replication batch - undefined if batch didn't cause a db write. */
    newLastUpdated?: number;
};

const systemActionArgSchemas = {
    [APPLY_MESSAGES_ACTION]: SB.array(SB.string()),
    [HEARTBEAT_ACTION]: SB.object({ lastLoginAt: SB.int().optional() }),
};

export default class ServerReplicantReplicate<T extends Replicant> {
    private db: DB<T['state'], T['messages']>;

    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            applyMessagesToState: ApplyMessagesToStateFn;
            config: ReplicantConfig<T>;
            createActionAPI: CreateActionAPIFn<T>;
            db: DB<T['state'], T['messages']>;
            errorReporter: ErrorReporter;
            getApplicableMessages: (messages: UserMessageItem[]) => UserMessageItem[];
            writeChangeset: WriteChangesetFN;
            fetcher: Fetcher;
        },
    ) {
        this.db = opts.db;
    }

    async handleReplicateRequest(opts: {
        /**
         * The dynamic A/B test config used by the client for manual assignment
         *
         * Client config is used when replicating actions server-side to avoid out-of-sync
         * errors on mid-session dynamic A/B test config update.
         */
        abTestsDynamicConfig: ABTestsDynamicConfig;

        clientMetainfo?: ClientMetainfoState;
        id: string;
        pushDocToIndex: (userId: string, doc: { [key: string]: unknown }) => Promise<void>;
        queue: ReplicationEntry[];
        rev: number;
        sessionId: string;
        sessionName?: string;
        requestedProfileIds?: string[];
        stateContext: any;

        /** Client's PRNG state after executing actions. */
        clientRandomSeed: number;

        /**
         * Client generated request id -- used to validate whether requests are being replayed by
         * the infrastructure
         */
        clientRequestId: string;

        /** These are used when fetching states, to determine whether to fetch consistently. */
        consistentFetchIds: string[];
    }): Promise<ReplicationResult<T['state'], T['ruleset']>> {
        logger.debug('REPLICANT: replicate ', opts);

        const { id, queue, rev, stateContext } = opts;

        const isHeartbeatOnlyRequest = queue.every((action) => action.fn === HEARTBEAT_ACTION);

        let rescheduleOnSessionEndPromise: Promise<void> | undefined;

        if (isHeartbeatOnlyRequest) {
            const actionArgs = queue[0]?.args;

            if (systemActionArgSchemas[HEARTBEAT_ACTION].isValid(actionArgs)) {
                rescheduleOnSessionEndPromise = this.rescheduleOnSessionEndEvent(id, {
                    lastLoginAt: actionArgs.lastLoginAt,
                    sessionId: opts.sessionId,
                });
            }

            const messages = await this.db.fetchMessages(id, { consistentRead: true });

            if (messages.length === 0) {
                const chatbotMetainfo = await this.db.chatbot.fetchMetainfo(id);

                await rescheduleOnSessionEndPromise;

                // In case of a heartbeat request, if the user has no unreduced messages we can skip state read and exit early:
                const chatbotPurchaseHistoryDelta = getChatbotPurchaseHistoryDelta(
                    opts.clientMetainfo,
                    chatbotMetainfo ?? {},
                );

                return {
                    lastUpdated: 0, // Unused by the client since the request contains no async actions
                    messages: [],
                    results: [],
                    rev,
                    metainfoDelta:
                        chatbotPurchaseHistoryDelta.length > 0
                            ? { chatbotPurchaseHistory: chatbotPurchaseHistoryDelta }
                            : undefined,
                };
            }
        }

        const {
            entry: persistedEntry,
            entryDiffs,
            messages,
        } = await this.db.fetch(id, { consistentRead: false }).then((fetchResult) => {
            if (fetchResult.entry && rev > fetchResult.entry.rev) {
                // Client knows about a revision from the future with respect to the server -- this means we're
                // hitting an inconsistent read. Hence, retry with consistent read.
                return this.db.fetch(id, { consistentRead: true });
            }

            return fetchResult;
        });

        if (!persistedEntry) {
            throw new Error('Entry not found for id: ' + id);
        }

        stateContext.id = id;
        stateContext.userData = persistedEntry;
        stateContext.messages = messages;

        // We've already played on a newer version. Playing here could unleash hell.
        if (persistedEntry.version > this.opts.config.migrator.getLatestVersion()) {
            throw new ReplicantError(
                `Version ${this.opts.config.version} is too low. Expected ${persistedEntry.metainfo.appVersion}!`,
                ReplicantErrorCode.version_error,
            );
        }

        const throwIfSessionDesync = () => {
            if (persistedEntry.metainfo.lastSessionId !== opts.sessionId) {
                const clientErrorProps = {
                    clientSessionName: opts.sessionName,
                    lastSessionName: persistedEntry.metainfo.lastSessionName,
                };

                throw new ReplicantError(
                    'Session desync: see more in https://docs.dev.gc-internal.net/replicant/faq/#how-to-fix-session-desync-errors.',
                    ReplicantErrorCode.session_desync_error,
                    undefined,
                    'fatal',
                    {
                        clientRev: rev,
                        clientSessionId: opts.sessionId,
                        serverRev: persistedEntry.rev,
                        serverLastSessionId: persistedEntry.metainfo.lastSessionId,
                    },
                    clientErrorProps,
                );
            }
        };

        if (persistedEntry.rev !== rev) {
            throwIfSessionDesync();

            // Special case where persistedEntry.rev is just one ahead of what the client reports.
            if (persistedEntry.rev === rev + 1) {
                // The server is 1 step ahead. Check the issues with it.
                const changeset =
                    (await this.opts.db.fetchChangeset(id, persistedEntry.rev)) ??
                    (await this.opts.db.fetchChangeset(id, persistedEntry.rev, { consistentRead: true }));
                if (!changeset || Array.isArray(changeset.replication)) {
                    throw new Error('Could not fetch changeset for id: ' + id + ', rev: ' + rev);
                }

                if (
                    persistedEntry.metainfo.lastClientRequestId &&
                    persistedEntry.metainfo.lastClientRequestId !== opts.clientRequestId
                ) {
                    const actionsQueue = queue.filter((x) => x.fn !== HEARTBEAT_ACTION);
                    if (!deepEquals(changeset.replication.queue, actionsQueue)) {
                        throwIfSessionDesync();

                        // If not desync, throw a mismatch error.
                        throw new ReplicantError(
                            'Client revision number does not match the latest revision known to server. This may be caused by a race condition.',
                            ReplicantErrorCode.replication_error,
                            ReplicantErrorSubCode.retry_queue_mismatch,
                            'error',
                            {
                                clientRev: rev,
                                clientQueue: actionsQueue,
                                serverRev: persistedEntry.rev,
                                serverQueue: changeset.replication.queue,
                            },
                        );
                    }
                }

                const hasAsyncAction = queue.some((action) => action.async);

                return {
                    messages: hasAsyncAction ? (changeset.messages as Message<T['messages']>[]) : [], // Replication response only includes reduced messages in case of an async action
                    results: changeset.replication.results,
                    rev: persistedEntry.rev,
                    lastUpdated: persistedEntry.lastUpdated,
                };
            }

            throw new ReplicantError(
                'Client revision number does not match the latest revision known to server. This may be caused by a race condition.',
                ReplicantErrorCode.replication_error,
                ReplicantErrorSubCode.rev_id_mismatch,
                'error',
                { clientRev: rev, serverRev: persistedEntry.rev },
            );
        }

        // If the document version is not migrated, then the user would need to relogin or in this case, even refresh
        // the game to load the new client.
        if (persistedEntry.version !== this.opts.config.migrator.getLatestVersion()) {
            throw new Error('Invalid user document version: ' + persistedEntry.version);
        }

        rescheduleOnSessionEndPromise =
            rescheduleOnSessionEndPromise ??
            this.rescheduleOnSessionEndEvent(id, {
                lastLoginAt: persistedEntry.lastLoginAt,
                sessionId: opts.sessionId,
            });

        const replicateResult = await this.replicate({
            abTestsDynamicConfig: opts.abTestsDynamicConfig,
            clientMetainfo: opts.clientMetainfo,
            clientRandomSeed: opts.clientRandomSeed,
            clientRequestId: opts.clientRequestId,
            consistentFetchIds: opts.consistentFetchIds,
            entry: persistedEntry,
            entryDiffs,
            id,
            messages,
            queue,
            pushDocToIndex: opts.pushDocToIndex,
            requestedProfileIds: opts.requestedProfileIds,
            sessionId: opts.sessionId,
            sessionName: opts.sessionName,
        });

        await rescheduleOnSessionEndPromise;

        return {
            lastUpdated: replicateResult.lastUpdated,
            messages: replicateResult.messages,
            results: replicateResult.results,
            rev: replicateResult.entry.rev,
            metainfoDelta: replicateResult.metainfoDelta,
            newLastUpdated: replicateResult.newLastUpdated,
            requestedProfiles: replicateResult.requestedProfiles,
        };
    }

    async replicate(opts: {
        abTestsDynamicConfig: ABTestsDynamicConfig;
        clientMetainfo?: ClientMetainfoState;
        clientRandomSeed?: number;
        clientRequestId?: string;
        consistentFetchIds: string[];
        entry: Entry<T['state']>;
        entryDiffs: EntryDiff[];
        id: string;
        messages: UserMessageItem<T['messages']>[];
        pushDocToIndex?: (userId: string, doc: { [key: string]: unknown }) => Promise<void>;
        queue: ReplicationEntry[];
        requestedProfileIds?: string[];
        sessionId?: string;
        sessionName?: string;
    }): Promise<Omit<ReplicationResult<T['state'], T['ruleset']>, 'rev'> & { entry: Entry<T['state']> }> {
        const { clientRandomSeed, consistentFetchIds, entry, entryDiffs, requestedProfileIds, id, messages, queue } =
            opts;

        let virtualEntry: VirtualEntry<T['state']>;

        try {
            virtualEntry = createVirtualEntry(this.opts.config, entry);
        } catch (e) {
            throw new ReplicantError(
                `Replication error: Invalid state: ${JSON.stringify(entry.state)}.`,
                'replication_error',
                'invalid_state',
            );
        }

        const reducedMessages: UserMessageItem[] = [];
        const unreducedMessages: UserMessageItem[] = [];

        const userData = virtualEntry.entry;
        const lastUpdated = userData.lastUpdated;

        // Store a copy of API metainfo properties so we can send new values back to the client in case of changes.
        const oldPaymentSubscriptionStatus = deepCopy(userData.metainfoMVCC.paymentSubscription);
        const oldPurchaseHistoryLength = userData.metainfo.purchaseHistory?.length;
        const oldHasPhoneNumber = userData.metainfo.hasPhoneNumber;

        const oldLastInvokeTime = userData.lastInvokeTime;

        // For indexing purposes, compute the old document to see if we need to push a new one after changes.
        const oldIndexDoc =
            opts.pushDocToIndex && JSON.stringify(generateIndexDoc(id, userData, this.opts.config, () => lastUpdated));

        // Results of async calls that need to communicated back to the client to replay the state.
        const results: ChangesetResult[][] = [];

        const delayedActions: DelayedAction[] = [];

        // Populate user state with system fields:
        addNonEnumerableSystemStateFields(
            userData.state,
            getSystemStateFieldsFromEntry(id, userData),
            this.opts.config,
        );

        // Validate system action arguments.
        queue.forEach((entry) => {
            if (entry.fn in systemActionArgSchemas) {
                const schema = systemActionArgSchemas[entry.fn as keyof typeof systemActionArgSchemas];

                schema.tryValidate(entry.args);
            }
        });

        const isSampled = perf.isSampled();

        if (opts.clientRequestId) {
            // Stamp new client requestId.
            virtualEntry.entry.metainfo.lastClientRequestId = opts.clientRequestId;
        }

        // Apply replication queue.
        for (const entry of queue) {
            if (entry.fn === HEARTBEAT_ACTION) {
                continue;
            }

            const isApplyMessagesAction = entry.fn === APPLY_MESSAGES_ACTION;

            // Check if the requested action exists and is of the correct type.
            const action = getNamespacedValue(this.opts.config.actions, entry.fn)!;

            if (isApplyMessagesAction) {
                if (action) {
                    throw new ReplicantError(
                        'System actions should not be configurable.',
                        'replication_error',
                        'invalid_action',
                    );
                }
            } else if (
                !action ||
                (action.type === 'async' && !entry.async) ||
                (action.type === 'sync' && entry.async)
            ) {
                throw new ReplicantError(
                    'Action ' + entry.fn + ' not found or async flag not correct!',
                    'replication_error',
                    'invalid_action',
                );
            }

            // Report Sentry warnings on oversized arguments:
            if (entry.args) {
                const argsSize = JSON.stringify(entry.args).length;

                if (argsSize > ARGS_TOO_LARGE_WARNING_LIMIT_BYTES) {
                    const warning = createActionArgsTooLargeWarning({ actionName: entry.fn, argsSize });
                    await this.opts.errorReporter.captureException(warning, { user: { id } });
                }
            }

            const now = this.opts.apiAccess.dateNow();
            const invokeTime = entry.meta ? entry.meta.now : now;

            // Prep the API objects passed to actions.
            const apiOpts = {
                abTestsDynamicConfig: opts.abTestsDynamicConfig,
                userId: id,
                entry: userData,
                consistentFetchIds,
                delayedActions,
                invokeTime,
                sessionId: opts.sessionId,
                fetcher: this.opts.fetcher,
            };

            // Validate server time.
            if (!userData.metainfo.clockOffset && now < userData.lastUpdated - MAX_CLIENT_SYNC_CLOCK_OFFSET) {
                const errorMessage =
                    `Current server time could not be validated: ${now}: ` +
                    `offset to lastUpdated: ${now - userData.lastUpdated}`;

                throw new ReplicantError(errorMessage, 'replication_error', 'client_time_invalid');
            }

            if (entry.meta) {
                const lastInvokeTime = userData.lastInvokeTime || 0;

                // Validate invoke time.
                if (
                    !userData.metainfo.clockOffset &&
                    (invokeTime < lastInvokeTime || invokeTime > now + MAX_CLIENT_SYNC_CLOCK_OFFSET)
                ) {
                    if (invokeTime > now + MAX_LARGE_CLIENT_SYNC_CLOCK_OFFSET) {
                        const errorMessage =
                            `Very large offset to now: ${invokeTime - now}. ` +
                            `Offset to lastInvokeTime: ${invokeTime - lastInvokeTime} `;

                        throw new ReplicantError(errorMessage, 'replication_error', 'client_time_offset_too_large');
                    } else {
                        const errorMessage =
                            `Client supplied time could not be validated: ${invokeTime}: ` +
                            `offset to lastInvokeTime: ${invokeTime - lastInvokeTime} ` +
                            `and offset to now: ${invokeTime - now}.`;

                        throw new ReplicantError(errorMessage, 'replication_error', 'client_time_invalid');
                    }
                }

                userData.lastInvokeTime = invokeTime;
            }

            const performanceContext: perf.PerformanceContext = { type: 'action', name: entry.fn, userId: id };

            if (!entry.async) {
                const api = await this.createActionAPI({
                    ...apiOpts,
                    async: false,
                    isServerSide: entry.isServerSide === true,
                });

                if (isApplyMessagesAction) {
                    const args = entry.args as string[];

                    const messagesToApply = args.map((messageToApplyId) => {
                        const messageToApply = messages.find((message) => messageToApplyId === message.id);

                        // Sanity check. Also, type check.
                        if (!messageToApply) {
                            throw new ReplicantError(
                                'Cannot apply a non-existent message',
                                'replication_error',
                                'missing_messages',
                                'error',
                                { messageId: messageToApplyId },
                            );
                        }

                        return messageToApply;
                    });

                    const handledMessages = this.opts.applyMessagesToState(id, userData, messagesToApply, {
                        isSampled,
                    });
                    reducedMessages.push(...handledMessages.reducedMessages);
                    unreducedMessages.push(...handledMessages.unreducedMessages);
                } else {
                    const actionFn = isSampled
                        ? perf.withPerformanceTracking(action.fn, performanceContext)
                        : action.fn;

                    for (let i = 0; i < (entry.times ?? 1); i++) {
                        actionFn(userData.state, entry.args, api);
                    }
                }
            } else {
                if (entry.times !== undefined) {
                    throw Error('Unexpected `times` property in async action entry');
                }

                const api = await this.createActionAPI({
                    ...apiOpts,
                    async: true,
                    isServerSide: entry.isServerSide === true,
                });

                const replicantApis = wrapAPI(api);

                if (isSampled) {
                    perf.wrapWithPerformanceTracking(replicantApis.api, performanceContext);
                }

                replicantApis.api.hasUsedAsyncAPIs = false;

                const actionFn = isSampled ? perf.withPerformanceTracking(action.fn, performanceContext) : action.fn;

                const asyncResult = actionFn(userData.state, entry.args, replicantApis.api);

                if (replicantApis.api.hasUsedAsyncAPIs && !isPromise(asyncResult)) {
                    throw new ReplicantError(
                        'Async action `' + entry.fn + '` uses asynchronous APIs, but does not return a promise.',
                        'replication_error',
                        'invalid_action',
                    );
                }

                await asyncResult;

                if (replicantApis.api.hasUsedAsyncAPIs && replicantApis.results.some((x) => x.async && !x.resolved)) {
                    throw new ReplicantError(
                        `Async action ${entry.fn} has unresolved async api results. Make sure to resolve all the asynchronous api method calls: hanging promises are forbidden in Replicant code.`,
                        'replication_error',
                        'invalid_action',
                    );
                }

                // Push results on the stack eating the resolved flag that is used only for cross checking whether
                // async actions have all the results resolved.
                results.push(replicantApis.results.map(({ value, async, failed }) => ({ value, async, failed })));
            }
        }

        // Validate that random seed is synchronized between client and server.
        if (clientRandomSeed !== undefined && userData.metainfo.random.n !== clientRandomSeed) {
            throw new ReplicantError(
                `Client's random seed doesn't match server's random seed after executing actions. ` +
                    `client: ${clientRandomSeed}, server: ${userData.metainfo.random.n}\n` +
                    'See https://docs.dev.gc-internal.net/replicant/faq/index.html#how-to-fix-random-seed-errors for more.',
                'replication_error',
                'random_seed_invalid',
            );
        }

        // Validate that the user entry is valid.
        try {
            this.opts.config.stateSchema.tryValidate(userData.state);
        } catch (err: any) {
            throw new ReplicantError(
                `Applying actions ${JSON.stringify(queue.map((x) => x.fn))} yields error: ${err.message}`,
                'replication_error',
                'invalid_action',
            );
        }

        /** Messages that were successfully applied to user state but failed to delete from DB on an earlier replication request. */
        const previouslyReducedMessages: UserMessageItem[] = [];

        const applicableMessages = this.opts.getApplicableMessages(messages).filter((m) => {
            const isReducedByApplyMessagesAction = reducedMessages.find(({ id }) => id === m.id);

            const isPreviouslyReduced = isMessageRecentlyReduced(virtualEntry.entry.metainfo, m);

            if (isPreviouslyReduced) {
                previouslyReducedMessages.push(m);
            }

            return !isPreviouslyReduced && !isReducedByApplyMessagesAction;
        });

        const hasAsyncAction = queue.some((entry) => entry.async);
        if (hasAsyncAction) {
            // Try to apply any outstanding messages.
            const handledMessages = this.opts.applyMessagesToState(id, userData, applicableMessages, { isSampled });
            reducedMessages.push(...handledMessages.reducedMessages);
            unreducedMessages.push(...handledMessages.unreducedMessages);
        }

        // Execute delayed messages, action scheduling and reporting from the sync APIs
        const resolvedUserActions = queue.map((entry) => entry.fn).filter((actionName) => !isSystemAction(actionName));

        // Only track performance in delayed actions if the action batch consists of a single action:
        const delayedActionsPerformanceContext: perf.PerformanceContext | undefined =
            isSampled && resolvedUserActions.length === 1
                ? { type: 'action', name: resolvedUserActions[0]!, userId: id }
                : undefined;

        const executionContext: ExecutionContext = {
            entryPromises: { [id]: Promise.resolve(userData) },
            performanceTracking: { context: delayedActionsPerformanceContext },
        };
        await this.opts.apiAccess.resolveDelayedActions(delayedActions, executionContext);

        let requestedProfiles;
        if (requestedProfileIds && requestedProfileIds.length > 0) {
            // After posting messages, we want to return the updated states to the client.
            const fetchStateResults = await this.opts.apiAccess.fetchStates(requestedProfileIds, {
                consistentFetchIds,
            });
            requestedProfiles = fetchStateResults.states;
        }

        const actions = queue.filter((x) => x.fn !== HEARTBEAT_ACTION);

        const unreducedMessagesToDelete = getUnreducedMessagesForDeletion(
            this.opts.apiAccess.dateNow(),
            unreducedMessages,
        );

        if (actions.length > 0 || reducedMessages.length > 0) {
            userData.rev++;
            userData.lastUpdated = this.opts.apiAccess.dateNow();

            // Update app version.
            userData.metainfo.appVersion = this.opts.config.version;

            // Update sessionId.
            userData.metainfo.lastSessionName = opts.sessionName;
            userData.metainfo.lastSessionId = opts.sessionId;

            updateRecentlyReducedMessages(this.opts.apiAccess.dateNow(), userData.metainfo, reducedMessages);

            const recoverySnapshotUpdate = updateSnapshotTimestamps({
                entry: userData,
                now: userData.lastUpdated,
            });

            const virtualSnapshot = virtualEntry.snapshot();

            // Writing the change.
            const { itemByteSize } = await this.db.writeEntryOrEntryDiff({
                lastInvokeTime: userData.lastInvokeTime === oldLastInvokeTime ? undefined : userData.lastInvokeTime,
                oldEntryDiffs: entryDiffs,
                recoverySnapshotUpdate,
                snapshot: virtualSnapshot,
                userId: id,
            });

            if (itemByteSize > 300_000) {
                // report to sentry
                await this.opts.errorReporter.captureException(
                    new ReplicantError(
                        'User entry or entry diff size above 300KB. Reduce state size to avoid hitting the 400KB hard limit!',
                        'server_error',
                        'state_too_large',
                        'fatal',
                    ),
                    { user: { id } },
                    { itemByteSize },
                );
            }

            // We won the write for this `rev`, hence we can write the changeset and delete reduced messages.
            // This call should normally never fail, so ideally, we don't need to wait for its confirmation
            // before returning the result to user.
            const messagesToDelete = [...reducedMessages, ...unreducedMessagesToDelete, ...previouslyReducedMessages];

            const dbPromises = Promise.all([
                this.opts
                    .writeChangeset({
                        id,
                        date: new Date(virtualSnapshot.entry.lastUpdated),
                        entry: virtualSnapshot.entry,
                        replication: { queue: actions, results },
                        messages: reducedMessages,
                        diff: virtualSnapshot.diff,
                        snapshotUpdate: recoverySnapshotUpdate,
                    })
                    .catch((error) =>
                        this.opts.errorReporter.captureException(
                            error,
                            { user: { id } },
                            {
                                diffJSONLength: JSON.stringify(virtualSnapshot.diff).length,
                                messagesJSONLength: JSON.stringify(reducedMessages).length,
                            },
                        ),
                    ),

                messagesToDelete.length > 0
                    ? this.db
                          .deleteMessages(id, messagesToDelete)
                          .catch((error) =>
                              this.opts.errorReporter.captureException(
                                  error,
                                  { user: { id } },
                                  { messagesToDeleteLength: messagesToDelete.length },
                              ),
                          )
                    : undefined,
            ]);

            // State has been changed, notify indexer
            if (opts.pushDocToIndex) {
                const newIndexDoc = generateIndexDoc(id, userData, this.opts.config, this.opts.apiAccess.dateNow);

                // JSON stringify comparison if typically faster than utils.deepEquals, especially when the common case
                // is that they're more likely to be equal rather than not equal.
                // Note: we need this because of the arrays.
                if (JSON.stringify(newIndexDoc) !== oldIndexDoc) {
                    await opts.pushDocToIndex(id, newIndexDoc);
                }
            }

            // Note: Because of the limitations of how API Gateway <-> Lambda integration works, we cannot return a
            // result to the user and keep doing work after that, since the Lambda is frozen and the work may not
            // complete.
            // This is less than ideal, since we're slowing down the response to user with no good reason.
            await dbPromises;
        } else if (unreducedMessagesToDelete.length > 0 || previouslyReducedMessages.length > 0) {
            const messagesToDelete = [...unreducedMessagesToDelete, ...previouslyReducedMessages];

            await this.db
                .deleteMessages(id, messagesToDelete)
                .catch((error) =>
                    this.opts.errorReporter.captureException(
                        error,
                        { user: { id } },
                        { messagesToDeleteLength: messagesToDelete.length },
                    ),
                );
        }

        const newHasPhoneNumber = userData.metainfo.hasPhoneNumber;
        const hasPhoneNumberDelta = newHasPhoneNumber !== oldHasPhoneNumber ? newHasPhoneNumber : undefined;

        const newPaymentSubscriptionStatus = userData.metainfoMVCC.paymentSubscription;
        const paymentSubscriptionStatusDelta =
            oldPaymentSubscriptionStatus !== newPaymentSubscriptionStatus ? newPaymentSubscriptionStatus : undefined;

        const newPurchaseHistoryLength = userData.metainfo.purchaseHistory?.length;
        const purchaseHistoryDelta =
            oldPurchaseHistoryLength !== newPurchaseHistoryLength
                ? userData.metainfo.purchaseHistory?.slice(oldPurchaseHistoryLength)
                : undefined;

        const responseMessages = hasAsyncAction
            ? // The client should reduce all messages the server just reduced.
              reducedMessages
            : // The client should reduce all messages that the server did not reduce.
              applicableMessages.filter((x) => !reducedMessages.includes(x));

        return {
            entry: userData,
            results,
            messages: responseMessages,
            metainfoDelta: {
                hasPhoneNumber: hasPhoneNumberDelta,
                paymentSubscriptionStatus: paymentSubscriptionStatusDelta,
                purchaseHistory: purchaseHistoryDelta,
                chatbotPurchaseHistory: getChatbotPurchaseHistoryDelta(
                    opts.clientMetainfo,
                    userData.chatbotMetainfo ?? {},
                ),
            },
            requestedProfiles,
            lastUpdated,
            newLastUpdated: userData.lastUpdated > lastUpdated ? userData.lastUpdated : undefined,
        };
    }

    private createActionAPI: CreateActionAPIFn<T> = async (opts) => {
        const api = await this.opts.createActionAPI(opts);
        if (!this.opts.config.sendActionAnalyticsWithClient || opts.isServerSide) return api;

        // Shim the API to filter out analytics events that are sent by the client
        const isServerAnalyticsEvent = (event: ActionAnalyticsEvent) => event.userId && event.userId !== opts.userId;
        return {
            ...api,
            sendAnalyticsEvents: (events) => api.sendAnalyticsEvents(events.filter(isServerAnalyticsEvent)),
        };
    };

    private async rescheduleOnSessionEndEvent(
        userId: string,
        opts: { lastLoginAt: number | undefined; sessionId: string | undefined },
    ): Promise<void> {
        const hasOnSessionEndEventHandler = !!this.opts.config.eventHandlers?.onSessionEnd;

        if (!hasOnSessionEndEventHandler && this.opts.config.disableExitFinalAnalytics) {
            return;
        }

        const DELAY_IN_MS = duration({ minutes: 4 }); // The `.schedule` API always adds 1 minute on top of this delay so the event gets scheduled to `now + 5 minutes`, floored down to the latest minute.

        const sessionDurationMs = opts.lastLoginAt ? this.opts.apiAccess.dateNow() - opts.lastLoginAt : undefined;

        try {
            const metainfoMVCC = await this.db.metainfoMVCC.fetchMetainfoMVCC(userId);

            await this.opts.apiAccess.systemScheduledActions.schedule.onSessionEnd({
                args: { sessionDurationMs, sessionId: opts.sessionId },
                delayInMS: DELAY_IN_MS,
                notificationId: 'onSessionEnd',
                userData: { metainfoMVCC },
                userId,
            });
        } catch (error: any) {
            await this.opts.errorReporter.captureException(error, { user: { id: userId } });
        }
    }
}

function isSystemAction(actionName: string): boolean {
    return actionName === APPLY_MESSAGES_ACTION || actionName === HEARTBEAT_ACTION;
}

function getChatbotPurchaseHistoryDelta(
    clientMetainfo: ClientMetainfoState | undefined,
    chatbotMetainfo: ChatbotMetainfo,
): PurchaseInfo[] {
    const purchaseHistory = chatbotMetainfo.purchaseHistory ?? [];
    const clientLength = clientMetainfo?.chatbotPurchaseHistoryLength ?? 0;

    if (purchaseHistory.length > clientLength) {
        return purchaseHistory.slice(clientLength);
    }

    return [];
}
